import * as THREE from "three";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

import { createTextureFromWrapperAsync, isGifTexture, forgetPendingUnmute } from "@/scripts/utils/webgl/TextureFactory";
import { domRectToWorld } from "@/scripts/utils/webgl/DOMTracker";
import { createShaderMaterial, syncCursorWarp } from "@/scripts/utils/webgl/ShaderMaterial";
import { ShaderPlaneGroup, tvShader } from "@/scripts/utils/webgl/ShaderPlaneGroup";
import { waitForElementSize } from "./waitForElementSize";
import { getMarqueeVelocity } from "../marquee";
import { prefersReducedMotion } from "../variables";

const marqueeCurveStrength = 10;
const marqueeParallaxSpeed = 0.1;

export const marqueeBend = { max: 10, fullSpeed: 10, power: 4 };

interface TrackedPlane {
    el: HTMLElement;
    mesh: THREE.Mesh;
    texture: THREE.Texture;
    scaleMultiplier: number;
    marqueeWrapper: HTMLElement | null;
    scrollTrigger?: ScrollTrigger;
    squircleScrollTrigger?: ScrollTrigger;
    anchorEl?: HTMLAnchorElement | null;
    handleAnchorEnter?: (e: PointerEvent) => void;
    handleAnchorLeave?: (e: PointerEvent) => void;
    handleMove?: (e: MouseEvent) => void;
    pressEl?: HTMLElement;
    handlePress?: (e: PointerEvent) => void;
    pressAbort?: AbortController;
}

export class PlanePool extends ShaderPlaneGroup {
    protected getMeshes() {
        return [...this.planes.values()].map(p => p.mesh);
    }

    setMarqueeUniform(name: string, value: number): void {
        for (const plane of this.planes.values()) {
            if (!plane.marqueeWrapper) continue;
            (plane.mesh.material as THREE.ShaderMaterial).uniforms[name].value = value;
        }
    }

    private planes: Map<HTMLElement, TrackedPlane> = new Map();
    private pending: Set<HTMLElement> = new Set();
    private scene: THREE.Scene;
    private mutObs: MutationObserver | null = null;
    private introDelay: number = 0;
    private introGate: Promise<void> = Promise.resolve();
    private controllers: Map<HTMLElement, AbortController> = new Map();

    // Walk up from el until we find an ancestor whose parent has multiple children containing [data-webgl]
    private getStaggerIndex(el: HTMLElement): number {
        let parent = el.parentElement;
        while (parent && parent !== this.root) {
            const siblings = [...parent.querySelectorAll<HTMLElement>("[data-webgl]")];
            if (siblings.length > 1) return siblings.indexOf(el);
            parent = parent.parentElement;
        }
        return 0;
    }

    private setupIntro(plane: TrackedPlane): void {
        const staggerIndex = this.getStaggerIndex(plane.el);
        const isInitial = plane.el.getBoundingClientRect().top < window.innerHeight;
        const delay = isInitial ? this.introDelay + staggerIndex * tvShader.intro.stagger : 0;

        plane.squircleScrollTrigger = ScrollTrigger.create({
            trigger: plane.el,
            start: "top 100%",
            once: true,
            onEnter: () => {
                const mat = plane.mesh.material as THREE.ShaderMaterial;

                const { rest, intro } = tvShader;

                mat.uniforms.uSquircleProgress.value = intro.squircle;
                mat.uniforms.uLensDistortion.value = intro.lens;
                mat.uniforms.uEdgeBump.value = intro.edge;

                gsap.to(mat.uniforms.uSquircleProgress, { value: rest.squircle, duration: intro.duration, delay, ease: "power2.inOut" });
                gsap.to(mat.uniforms.uLensDistortion, { value: rest.lens, duration: intro.duration - 0.2, delay, ease: "power3.out" });
                gsap.to(mat.uniforms.uFocusFactor, { value: rest.focus, duration: 1.2, delay, ease: "power3.out" });
                gsap.to(mat.uniforms.uEdgeBump, { value: rest.edge, duration: intro.duration, delay: delay + 0.2, ease: "power3.out" });

                gsap.to(plane, { scaleMultiplier: 1, duration: 1, delay, ease: "power3.out" });
            },
        });
    }

    private settleLens(mat: THREE.ShaderMaterial, lensState: { squircle: number; lens: number; focus: number; edge: number }, duration: number): void {
        gsap.to(mat.uniforms.uEdgeBump, { value: lensState.edge, duration, ease: "expo.out", overwrite: true });
        gsap.to(mat.uniforms.uLensDistortion, { value: lensState.lens, duration, ease: "expo.out", overwrite: true });
        gsap.to(mat.uniforms.uFocusFactor, { value: lensState.focus, duration, ease: "expo.out", overwrite: true });
        gsap.to(mat.uniforms.uSquircleProgress, { value: lensState.squircle, duration, ease: "expo.out", overwrite: true });
    }

    private async trackSingle(el: HTMLElement): Promise<TrackedPlane | null> {
        if (this.planes.has(el) || this.pending.has(el)) return null;
        this.pending.add(el);

        const controller = new AbortController();
        this.controllers.set(el, controller);

        const texturePromise = createTextureFromWrapperAsync(el);

        try {
            const [result, size] = await Promise.all([texturePromise, waitForElementSize(el, controller.signal)]);

            this.pending.delete(el);
            this.controllers.delete(el);
            if (!result) return null;
            if (!this.root.contains(el)) {
                result.texture.dispose();
                return null;
            }

            const ns = el.closest<HTMLElement>("[data-barba-namespace]")?.dataset.barbaNamespace;
            const isWrapping = ns === "work" || ns === "news";
            const marqueeWrapper = el.closest<HTMLElement>(".swiper-wrapper");
            const isCurved = isWrapping || (!!marqueeWrapper && !prefersReducedMotion);
            const meshAspect = size.width / size.height;
            const imageAspect = result.aspect;

            const geo = new THREE.PlaneGeometry(1, 1, isCurved ? 32 : 1, isCurved ? 32 : 1);
            const mat = createShaderMaterial(result.texture, meshAspect, imageAspect);
            const mesh = new THREE.Mesh(geo, mat);
            mesh.renderOrder = 0;
            this.scene.add(mesh);

            mat.uniforms.uSquircleProgress.value = tvShader.intro.squircle;
            mat.uniforms.uLensDistortion.value = tvShader.intro.lens;
            mat.uniforms.uFocusFactor.value = 0.5;
            mat.uniforms.uSharpCorners.value = 1;
            mat.uniforms.uEdgeBump.value = tvShader.intro.edge;

            if (!isWrapping) {
                mat.uniforms.uCurveStrength.value = 0;
            }

            if (marqueeWrapper && !prefersReducedMotion) {
                mat.uniforms.uSlideCurveStrength.value = marqueeCurveStrength;
                mat.uniforms.uParallaxSpeedX.value = marqueeParallaxSpeed;
            }

            const plane: TrackedPlane = {
                el,
                mesh,
                texture: result.texture,
                marqueeWrapper,
                scaleMultiplier: 0.8,
            };

            // Anchor mouse interaction
            const anchorEl = (el.tagName === "A" ? el : el.closest("a")) as HTMLAnchorElement | null;
            const isWorkCardImg = el.classList.contains("work-card__img");
            const hoverLens = isWorkCardImg ? { ...tvShader.hover, squircle: tvShader.rest.squircle } : tvShader.hover;

            if (anchorEl) {
                const mouse = mat.uniforms.uMouse.value as THREE.Vector2;

                if (!isWorkCardImg) {
                    const handleMove = (e: MouseEvent) => {
                        const rect = anchorEl.getBoundingClientRect();
                        const x = (e.clientX - rect.left) / rect.width;
                        const y = -(e.clientY - rect.top) / rect.height;
                        gsap.to(mouse, { x, y, duration: 0.25, ease: "power3.out", overwrite: true });
                    };

                    anchorEl.addEventListener("mousemove", handleMove);
                    plane.handleMove = handleMove;
                }

                plane.anchorEl = anchorEl;

                if (!prefersReducedMotion) {
                    let isHovering = false;
                    let leaveCall: gsap.core.Tween | null = null;

                    const handleAnchorEnter = (e: PointerEvent) => {
                        if (e.pointerType === "touch") return;

                        isHovering = true;
                        leaveCall?.kill();
                        leaveCall = null;

                        this.settleLens(mat, hoverLens, 0.5);
                    };

                    const handleAnchorLeave = (e: PointerEvent) => {
                        if (e.pointerType === "touch") return;

                        isHovering = false;

                        gsap.to(mouse, { x: 0.5, y: 0.5, duration: 0.6, ease: "power3.out" });

                        leaveCall?.kill();
                        leaveCall = gsap.delayedCall(0.05, () => {
                            if (isHovering) return;

                            this.settleLens(mat, tvShader.rest, 0.4);
                        });
                    };

                    anchorEl.addEventListener("pointerenter", handleAnchorEnter);
                    anchorEl.addEventListener("pointerleave", handleAnchorLeave);
                    plane.handleAnchorEnter = handleAnchorEnter;
                    plane.handleAnchorLeave = handleAnchorLeave;
                }
            }

            const pressEl = anchorEl ?? (marqueeWrapper ? el : null);

            if (pressEl && !prefersReducedMotion) {
                const handlePress = (e: PointerEvent) => {
                    if (e.pointerType !== "touch" || !e.isPrimary) return;

                    plane.pressAbort?.abort();
                    const pressAbort = new AbortController();
                    const { signal } = pressAbort;
                    plane.pressAbort = pressAbort;

                    const release = () => {
                        pressAbort.abort();
                        this.settleLens(mat, tvShader.rest, 0.4);
                    };

                    window.addEventListener(
                        "pointermove",
                        moveEvent => {
                            if (moveEvent.pointerId !== e.pointerId) return;
                            if (Math.hypot(moveEvent.clientX - e.clientX, moveEvent.clientY - e.clientY) > 10) release();
                        },
                        { passive: true, signal }
                    );
                    window.addEventListener(
                        "pointerup",
                        endEvent => {
                            if (endEvent.pointerId === e.pointerId) release();
                        },
                        { signal }
                    );
                    window.addEventListener(
                        "pointercancel",
                        endEvent => {
                            if (endEvent.pointerId === e.pointerId) release();
                        },
                        { signal }
                    );

                    this.settleLens(mat, hoverLens, 0.5);
                };

                pressEl.addEventListener("pointerdown", handlePress, { passive: true });
                plane.pressEl = pressEl;
                plane.handlePress = handlePress;
            }

            // Parallax ScrollTrigger
            const st = ScrollTrigger.create({
                trigger: el,
                start: "top bottom",
                end: "bottom top",
                scrub: true,
                onUpdate: self => {
                    const m = plane.mesh.material as THREE.ShaderMaterial;
                    m.uniforms.uParallax.value = self.progress;
                },
            });
            plane.scrollTrigger = st;

            this.planes.set(el, plane);
            return plane;
        } catch {
            this.pending.delete(el);
            this.controllers.delete(el);
            void texturePromise.then(result => result?.texture.dispose());
            return null;
        }
    }

    constructor(
        scene: THREE.Scene,
        private root: HTMLElement
    ) {
        super();
        this.scene = scene;
    }

    init(introDelay: number = 0, introGate?: Promise<void>): void {
        this.introDelay = introDelay;
        this.introGate = introGate ?? Promise.resolve();

        this.root.querySelectorAll<HTMLElement>("[data-webgl]").forEach(el => this.track(el));

        this.mutObs = new MutationObserver(mutations => {
            for (const mutation of mutations) {
                mutation.addedNodes.forEach(node => {
                    if (!(node instanceof HTMLElement)) return;
                    if (node.matches("[data-webgl]")) this.track(node);
                    node.querySelectorAll<HTMLElement>("[data-webgl]").forEach(el => this.track(el));
                });
                mutation.removedNodes.forEach(node => {
                    if (!(node instanceof HTMLElement)) return;
                    if (node.matches("[data-webgl]")) this.untrack(node);
                    node.querySelectorAll<HTMLElement>("[data-webgl]").forEach(el => this.untrack(el));
                });
            }
        });

        this.mutObs.observe(this.root, { childList: true, subtree: true });
    }

    replayIntros(): void {
        for (const plane of this.planes.values()) {
            this.killUniformTweens(plane.mesh.material as THREE.ShaderMaterial);
            plane.squircleScrollTrigger?.kill();
            this.setupIntro(plane);
        }
    }

    settleAll(lensState: { squircle: number; lens: number; focus: number; edge: number }): void {
        for (const plane of this.planes.values()) this.settleLens(plane.mesh.material as THREE.ShaderMaterial, lensState, 0.4);
    }

    getPlane(el: HTMLElement): TrackedPlane | undefined {
        return this.planes.get(el);
    }

    rebindRoot(newRoot: HTMLElement): void {
        this.root = newRoot;
        this.mutObs?.disconnect();
        this.mutObs?.observe(newRoot, { childList: true, subtree: true });
    }

    /** Call once per [data-webgl] element found in the DOM */
    track(el: HTMLElement): void {
        if (this.planes.has(el) || this.pending.has(el)) return;
        Promise.all([this.trackSingle(el), this.introGate]).then(([plane]) => {
            if (plane && this.planes.has(el)) this.setupIntro(plane);
        });
    }

    hidePlanesIn(container: HTMLElement): void {
        const materials = [...this.planes.values()].filter(plane => container.contains(plane.el)).map(plane => plane.mesh.material as THREE.ShaderMaterial);

        this.playChannelFlick(materials);
        materials.forEach(mat => {
            gsap.to(mat.uniforms.uOpacity, { value: 0, duration: 0.2, ease: "power2.in", overwrite: true });
        });
    }

    showPlanesIn(container: HTMLElement): void {
        for (const plane of this.planes.values()) {
            if (!container.contains(plane.el)) continue;

            const mat = plane.mesh.material as THREE.ShaderMaterial;
            gsap.to(mat.uniforms.uOpacity, { value: 1, duration: 0.4, ease: "expo.out", overwrite: true });
        }
    }

    syncAll(camera: THREE.PerspectiveCamera, rendererWidth: number, rendererHeight: number, time: number, scrollVelocity: number): void {
        for (const [el, plane] of this.planes) {
            const rect = el.getBoundingClientRect();
            const { x, y, width, height } = domRectToWorld(rect, camera, rendererWidth, rendererHeight);
            plane.mesh.position.set(x, y, 0);
            plane.mesh.scale.set(width * plane.scaleMultiplier, height * plane.scaleMultiplier, 1);

            const mat = plane.mesh.material as THREE.ShaderMaterial;
            mat.uniforms.uTime.value = time;
            mat.uniforms.uScrollVelocity.value = scrollVelocity;
            syncCursorWarp(mat, rect);

            const scrollDrive = prefersReducedMotion ? 0 : THREE.MathUtils.clamp(Math.abs(scrollVelocity) / 3, 0, 1);
            let scrollLip = scrollDrive * tvShader.scroll.page;

            if (plane.marqueeWrapper) {
                const bendDrive = THREE.MathUtils.clamp(-getMarqueeVelocity(plane.marqueeWrapper) / marqueeBend.fullSpeed, 0, 1);
                const bendCurve = Math.pow(bendDrive, marqueeBend.power);
                mat.uniforms.uSlideBend.value = marqueeBend.max * bendCurve;
                mat.uniforms.uParallaxX.value = THREE.MathUtils.clamp((rect.left + rect.width) / (rendererWidth + rect.width), 0, 1);

                if (!prefersReducedMotion) {
                    mat.uniforms.uParallaxSpeedX.value = marqueeParallaxSpeed * (1 + bendCurve * tvShader.scroll.sliderParallax);
                    scrollLip = Math.max(scrollLip, bendCurve * tvShader.scroll.slider);
                }
            }

            mat.uniforms.uScrollLip.value = THREE.MathUtils.lerp(mat.uniforms.uScrollLip.value, scrollLip, 0.15);
            if (Math.abs(mat.uniforms.uScrollLip.value - scrollLip) < 0.001) mat.uniforms.uScrollLip.value = scrollLip;
        }
    }

    untrack(el: HTMLElement): void {
        this.controllers.get(el)?.abort();
        this.controllers.delete(el);
        this.pending.delete(el);

        const plane = this.planes.get(el);
        if (!plane) return;

        plane.scrollTrigger?.kill();
        plane.squircleScrollTrigger?.kill();

        if (plane.anchorEl) {
            if (plane.handleAnchorEnter) plane.anchorEl.removeEventListener("pointerenter", plane.handleAnchorEnter);
            if (plane.handleAnchorLeave) plane.anchorEl.removeEventListener("pointerleave", plane.handleAnchorLeave);
            if (plane.handleMove) plane.anchorEl.removeEventListener("mousemove", plane.handleMove);
        }

        if (plane.pressEl && plane.handlePress) plane.pressEl.removeEventListener("pointerdown", plane.handlePress);
        plane.pressAbort?.abort();

        const video = el.querySelector<HTMLVideoElement>("video");
        if (video) {
            video.pause();
            video.removeAttribute("autoplay");
            forgetPendingUnmute(video);
        }

        this.killUniformTweens(plane.mesh.material as THREE.ShaderMaterial);
        gsap.killTweensOf(plane);
        this.scene.remove(plane.mesh);
        if (isGifTexture(plane.texture)) {
            plane.texture.play = false;
        }
        plane.texture.dispose();
        (plane.mesh.material as THREE.Material).dispose();
        plane.mesh.geometry.dispose();
        this.planes.delete(el);
    }

    dispose(): void {
        this.mutObs?.disconnect();
        for (const el of this.planes.keys()) this.untrack(el);
    }
}
